import { secureImageUrl } from "services/utils/payslipTemplate";
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  Svg,
  Path,
} from "@react-pdf/renderer";
import moment from "moment";
import Roboto from "../../../assets/fonts/Roboto/Roboto-Regular.ttf";
import RobotoBold from "../../../assets/fonts/Roboto/Roboto-Bold.ttf";
import { formatRate, getComponentRate } from "./otherEarningsFormat";

Font.register({
  family: "Roboto",
  fonts: [{ src: Roboto }, { src: RobotoBold, fontWeight: 600 }],
  format: "truetype",
});

// Blue and white only. Three weights of the one blue carry the whole sheet:
// BLUE fills the section headers, BLUE_TEXT sets every heading and figure that
// needs emphasis, and the two tints back the sub-headings and total rows.
const BLUE = "#173B7E"; // filled headers
const BLUE_TEXT = "#1B4FA8"; // headings, sub-totals, net pay
const BLUE_TINT = "#EDF3FD"; // sub-heading and sub-total rows
const BLUE_TINT_STRONG = "#DCE7FA"; // the TOTAL EARNINGS / TOTAL DEDUCTIONS bars
const BORDER = "#DCE5F2"; // every box outline
const RULE = "#EDF1F8"; // rules between data rows
const TEXT = "#1A2233"; // values
const LABEL = "#3F4B5F"; // field labels

const RUPEE = "₹";
const MINUS = "−";

const styles = StyleSheet.create({
  page: {
    padding: 18,
    fontFamily: "Roboto",
    color: TEXT,
    fontSize: 8,
    backgroundColor: "#FFFFFF",
  },
  card: {
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    padding: 8,
    backgroundColor: "#FFFFFF",
  },
  row: {
    flexDirection: "row",
  },

  /* ---------------- header ---------------- */
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 6,
  },
  logoBox: {
    width: 84,
    height: 42,
    justifyContent: "center",
  },
  logo: {
    width: 78,
    height: 40,
    objectFit: "contain",
  },
  companyBlock: {
    flex: 1,
    paddingRight: 10,
  },
  companyName: {
    fontSize: 13,
    fontWeight: 600,
    color: BLUE,
  },
  companyLine: {
    fontSize: 8,
    color: LABEL,
    marginTop: 2,
  },
  titleBlock: {
    width: 122,
    alignItems: "flex-end",
    borderLeft: `1px solid ${BORDER}`,
    paddingLeft: 10,
  },
  payslipTitle: {
    fontSize: 19,
    fontWeight: 600,
    color: BLUE,
    letterSpacing: 0.5,
  },
  payslipMonth: {
    fontSize: 12,
    fontWeight: 600,
    color: BLUE_TEXT,
    marginTop: 2,
  },

  /* ---------------- info cards ---------------- */
  infoCard: {
    width: "49.2%",
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
    backgroundColor: "#FBFCFF",
  },
  gap: {
    width: "1.6%",
  },
  cardHead: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingTop: 6,
    paddingBottom: 4,
  },
  chip: {
    width: 15,
    height: 15,
    borderRadius: 7.5,
    backgroundColor: BLUE_TEXT,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 6,
  },
  chipText: {
    fontSize: 8,
    fontWeight: 600,
    color: "#FFFFFF",
  },
  cardTitle: {
    fontSize: 8.5,
    fontWeight: 600,
    color: BLUE_TEXT,
    letterSpacing: 0.4,
  },
  cardBody: {
    paddingHorizontal: 9,
    paddingBottom: 6,
  },
  fieldRow: {
    flexDirection: "row",
    marginBottom: 3,
  },
  fieldLabel: {
    width: "44%",
    fontSize: 8,
    color: LABEL,
  },
  fieldColon: {
    width: 8,
    fontSize: 8,
    color: LABEL,
  },
  fieldValue: {
    flex: 1,
    fontSize: 8,
    color: TEXT,
  },

  /* ---------------- summary strip ---------------- */
  strip: {
    flexDirection: "row",
    marginTop: 6,
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
    backgroundColor: BLUE_TINT,
  },
  stripCell: {
    width: "25%",
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  stripDivider: {
    borderRight: `1px solid ${BORDER}`,
  },
  stripIcon: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: BLUE,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 7,
  },
  stripIconText: {
    fontSize: 9,
    fontWeight: 600,
    color: "#FFFFFF",
  },
  stripLabel: {
    fontSize: 7.5,
    fontWeight: 600,
    color: BLUE_TEXT,
    letterSpacing: 0.4,
  },
  stripValue: {
    fontSize: 11,
    fontWeight: 600,
    color: BLUE,
    marginTop: 3,
  },

  /* ---------------- earnings / deductions ---------------- */
  bodyRow: {
    flexDirection: "row",
    marginTop: 6,
  },
  bodyCol: {
    width: "49.2%",
  },
  tableBox: {
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
  },
  tableHead: {
    flexDirection: "row",
    paddingVertical: 5,
    paddingHorizontal: 8,
    backgroundColor: BLUE,
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
  },
  tableHeadText: {
    fontSize: 8.5,
    fontWeight: 600,
    color: "#FFFFFF",
    letterSpacing: 0.4,
  },
  // Sub-heading naming the group of rows under it ("Fixed Earnings").
  subHead: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: BLUE_TINT,
    borderBottom: `1px solid ${BORDER}`,
  },
  subHeadText: {
    fontSize: 8,
    fontWeight: 600,
    color: BLUE_TEXT,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 3.5,
    paddingHorizontal: 8,
    borderBottom: `1px solid ${RULE}`,
  },
  colName: {
    width: "58%",
    fontSize: 8,
    color: TEXT,
  },
  colAmount: {
    width: "42%",
    fontSize: 8,
    color: TEXT,
    textAlign: "right",
  },
  totalBar: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 5,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 6,
    border: `1px solid ${BORDER}`,
    backgroundColor: BLUE_TINT_STRONG,
  },
  totalBarLabel: {
    flex: 1,
    fontSize: 9.5,
    fontWeight: 600,
    color: BLUE,
    letterSpacing: 0.3,
  },
  totalBarValue: {
    fontSize: 9.5,
    fontWeight: 600,
    color: BLUE,
    textAlign: "right",
  },

  /* ---------------- salary summary band ---------------- */
  salaryBand: {
    marginTop: 6,
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: "#FBFCFF",
  },
  salaryRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  salaryTitleBlock: {
    width: 104,
    flexDirection: "row",
    alignItems: "center",
    borderRight: `1px solid ${BORDER}`,
    paddingRight: 8,
  },
  salaryTitle: {
    flex: 1,
  },
  salaryTitleText: {
    fontSize: 9,
    fontWeight: 600,
    color: BLUE_TEXT,
    letterSpacing: 0.3,
  },
  salaryTerm: {
    flex: 1,
    alignItems: "center",
    paddingHorizontal: 4,
  },
  salaryTermLabel: {
    fontSize: 8,
    color: LABEL,
  },
  salaryTermValue: {
    fontSize: 11,
    fontWeight: 600,
    color: TEXT,
    marginTop: 3,
  },
  salaryOperator: {
    width: 16,
    fontSize: 13,
    fontWeight: 600,
    color: BLUE_TEXT,
    textAlign: "center",
  },
  salaryNet: {
    flex: 1.35,
    alignItems: "center",
  },
  salaryNetLabel: {
    fontSize: 9,
    fontWeight: 600,
    color: BLUE_TEXT,
    letterSpacing: 0.3,
  },
  salaryNetValue: {
    fontSize: 15,
    fontWeight: 600,
    color: BLUE,
    marginTop: 3,
  },
  salaryWords: {
    fontSize: 8,
    color: LABEL,
    textAlign: "center",
    marginTop: 5,
  },

  /* ---------------- bottom summaries ---------------- */
  summaryHead: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingHorizontal: 7,
    backgroundColor: BLUE_TINT,
    borderTop: `1px solid ${BORDER}`,
    borderBottom: `1px solid ${BORDER}`,
  },
  summaryHeadText: {
    fontSize: 6.8,
    fontWeight: 600,
    color: BLUE_TEXT,
    letterSpacing: 0.2,
  },
  summaryRow: {
    flexDirection: "row",
    paddingVertical: 3.5,
    paddingHorizontal: 7,
    borderBottom: `1px solid ${RULE}`,
  },
  summaryTotalRow: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingHorizontal: 7,
    backgroundColor: BLUE_TINT,
  },
  summaryText: {
    fontSize: 7.5,
    color: TEXT,
  },
  summaryTextBold: {
    fontSize: 7.5,
    fontWeight: 600,
    color: BLUE_TEXT,
  },

  /* ---------------- footer ---------------- */
  footer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 6,
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
    paddingVertical: 7,
    paddingHorizontal: 9,
  },
  footerBadge: {
    width: 20,
    height: 20,
    borderRadius: 4,
    backgroundColor: BLUE_TINT,
    marginRight: 8,
  },
  footerNote: {
    flex: 1,
    fontSize: 7.5,
    color: BLUE_TEXT,
    lineHeight: 1.4,
    borderRight: `1px solid ${BORDER}`,
    paddingRight: 9,
  },
  footerRight: {
    width: 190,
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingLeft: 9,
  },
  footerLabel: {
    fontSize: 7.5,
    fontWeight: 600,
    color: BLUE_TEXT,
  },
  footerValue: {
    fontSize: 7.5,
    color: TEXT,
    marginLeft: 4,
  },
});

const isEmpty = (value) =>
  value === undefined || value === null || value === "";

const formatText = (value) => (isEmpty(value) ? "-" : `${value}`);

const toNumber = (value) => {
  if (isEmpty(value)) {
    return null;
  }
  const parsed = parseFloat(`${value}`.replace(/,/g, ""));
  return Number.isNaN(parsed) ? null : parsed;
};

// Indian-style grouped amount, e.g. 21144.36 -> "21,144.36".
const formatAmount = (value, decimals = 2) => {
  const parsed = toNumber(value);
  if (parsed === null) {
    return decimals ? "0.00" : "0";
  }
  return parsed.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatMoney = (value, decimals = 2) =>
  `${RUPEE} ${formatAmount(value, decimals)}`;

const parseDate = (value) =>
  moment(
    value,
    ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601],
    true,
  );

const formatDate = (value) => {
  const parsed = parseDate(value);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY") : formatText(value);
};

// "July 2026" for the masthead and the Pay Period field.
const formatPeriod = (payslipData) => {
  const parsed = parseDate(payslipData?.start_date || payslipData?.pay_date);
  return parsed.isValid()
    ? parsed.format("MMMM YYYY")
    : formatText(payslipData?.pay_schedule_name);
};

const formatCycle = (payslipData) => {
  const start = parseDate(payslipData?.start_date);
  const end = parseDate(payslipData?.end_date);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }
  return `${start.format("DD-MMM-YYYY")} to ${end.format("DD-MMM-YYYY")}`;
};

const pickNumber = (...values) => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed !== null) {
      return parsed;
    }
  }
  return null;
};

const getComponentName = (item) =>
  item?.component_name ?? item?.name ?? item?.component ?? item?.label ?? "";

// An Other Earnings row not mapped to a configured OT band shows the
// backend's raw "Default <band>" placeholder — "Default Band" for plain
// overtime, "Default Week Off" / "Default Holiday" for those categories
// (e.g. "Default Band Overtime", "Default Week Off Weekoff Attendance",
// "Default Holiday Holiday Attendance") — every one of them is the same
// unconfigured/factor-1 band and reads as "1 OT".
const DEFAULT_BAND_LABEL_PATTERN = /^Default\s+(?:Band|Week\s*Off|Holiday)\b/i;
const replaceDefaultBandLabel = (name) =>
  typeof name === "string" && DEFAULT_BAND_LABEL_PATTERN.test(name)
    ? name.replace(DEFAULT_BAND_LABEL_PATTERN, "1 OT").replace(/\s+/g, " ").trim()
    : name;

// Fixed Earnings names are left untouched — only the Other Earnings group.
const OTHER_EARNINGS_TITLE = "Other Earnings";
const displayComponentName = (item, groupTitle) => {
  const name = getComponentName(item);
  return groupTitle === OTHER_EARNINGS_TITLE
    ? replaceDefaultBandLabel(name)
    : name;
};

const getComponentAmount = (item) =>
  item?.amount ?? item?.component_amount ?? item?.value ?? item?.total;

const getComponentQty = (item) => item?.quantity ?? item?.qty ?? item?.count;

const normalizeItems = (...sections) =>
  sections
    .flatMap((section) => (Array.isArray(section) ? section : []))
    .filter((item) => item && getComponentName(item));

const sumComponentAmounts = (items) =>
  items.reduce(
    (sum, item) => sum + (toNumber(getComponentAmount(item)) || 0),
    0,
  );

/* ------------------------------------------------------------------ *
 * Overtime summary
 * ------------------------------------------------------------------ */

// HH:MM <-> minutes, so the OT total row adds up the way the "Net earnings by
// band" table in the pay run side peek does.
const parseTimeMinutes = (value) => {
  if (typeof value !== "string") return 0;
  const match = value.trim().match(/^(-)?\s*(\d+):(\d{2})$/);
  if (!match) return 0;
  const [, negativeSign, hours, minutes] = match;
  return (negativeSign ? -1 : 1) * (Number(hours) * 60 + Number(minutes));
};

const formatTimeMinutes = (value) => {
  if (!Number.isFinite(value)) return "00:00";
  const sign = value < 0 ? "-" : "";
  const absolute = Math.abs(value);
  return `${sign}${String(Math.floor(absolute / 60)).padStart(2, "0")}:${String(
    absolute % 60,
  ).padStart(2, "0")}`;
};

const normalizeKey = (value) =>
  `${value || ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");

const isOvertimeComponent = (item) =>
  /\bot\b|overtime|over time|week\s*off/i.test(getComponentName(item));

// The OT rows are the Other Earnings the pay run produced from hours worked.
// Earning hours come from the component's own quantity (that is what the
// amount was calculated on); the deduction hours come from `net_earnings_by_band`
// when a band name matches the component, and net is what is left of the two.
const buildOvertimeRows = (payslipData, otherEarningItems) => {
  const bands = payslipData?.net_earnings_by_band;
  const bandEntries =
    bands && typeof bands === "object" && !Array.isArray(bands)
      ? Object.entries(bands).filter(([name]) => name !== "Attendance")
      : [];

  const findBand = (componentName) => {
    const key = normalizeKey(componentName);
    if (!key) return null;
    // Longest matching band wins, so "1.5 OT" is preferred over "OT".
    return bandEntries
      .filter(([bandName]) => key.includes(normalizeKey(bandName)))
      .sort((a, b) => normalizeKey(b[0]).length - normalizeKey(a[0]).length)
      .map(([, bandData]) => bandData)[0];
  };

  return otherEarningItems.filter(isOvertimeComponent).map((item) => {
    const band = findBand(getComponentName(item));
    const earningMinutes = parseTimeMinutes(`${getComponentQty(item) ?? ""}`);
    const deductionMinutes = Math.abs(
      parseTimeMinutes(`${band?.total_deducted ?? ""}`),
    );
    return {
      name: replaceDefaultBandLabel(getComponentName(item)),
      earningMinutes,
      deductionMinutes,
      netMinutes: earningMinutes - deductionMinutes,
      rate: getComponentRate(item),
      amount: toNumber(getComponentAmount(item)) || 0,
    };
  });
};

// Overtime summary columns. Widths add up to 100% and are shared by the head,
// band and total rows so every value sits under its own heading.
const OT_COLUMNS = [
  { key: "type", head: "OT TYPE", width: "28%", align: "left" },
  { key: "earning", head: "EARNING HRS", width: "15%", align: "center" },
  { key: "deduction", head: "DEDUCTION HRS", width: "17%", align: "center" },
  { key: "net", head: "NET HRS", width: "12%", align: "center" },
  { key: "rate", head: `RATE (${RUPEE})`, width: "13%", align: "right" },
  { key: "amount", head: `AMOUNT (${RUPEE})`, width: "15%", align: "right" },
];

/* ------------------------------------------------------------------ *
 * Building blocks
 * ------------------------------------------------------------------ */

// Material-style glyphs on a 24x24 grid, drawn rather than imported so the
// sheet stays self-contained (a PDF cannot pull an icon font at render time).
const ICON_PATHS = {
  wallet:
    "M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z",
  calendar:
    "M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z",
  person:
    "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
  chart: "M5 9.2h3V19H5zM10.6 5h2.8v14h-2.8zm5.6 8H19v6h-2.8z",
  clock:
    "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z",
  document:
    "M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z",
};

const Icon = ({ name, size = 9, color = "#FFFFFF" }) => (
  <Svg viewBox="0 0 24 24" style={{ width: size, height: size }}>
    <Path d={ICON_PATHS[name]} fill={color} />
  </Svg>
);

// A section badge: the drawn glyph, or a symbol set in type when the glyph is
// simply a character (the rupee mark on the Net Salary tile).
const Chip = ({ icon, symbol, style, iconSize = 9, iconColor }) => (
  <View style={[styles.chip, style]}>
    {symbol ? (
      <Text style={styles.chipText}>{symbol}</Text>
    ) : (
      <Icon name={icon} size={iconSize} color={iconColor} />
    )}
  </View>
);

const InfoCard = ({ title, icon, fields }) => (
  <View style={styles.infoCard} wrap={false}>
    <View style={styles.cardHead}>
      <Chip icon={icon} />
      <Text style={styles.cardTitle}>{title}</Text>
    </View>
    <View style={styles.cardBody}>
      {fields.map((field) => (
        <View style={styles.fieldRow} key={field.label}>
          <Text style={styles.fieldLabel}>{field.label}</Text>
          <Text style={styles.fieldColon}>:</Text>
          <Text style={styles.fieldValue}>{formatText(field.value)}</Text>
        </View>
      ))}
    </View>
  </View>
);

// One column of the body: the filled EARNINGS / DEDUCTIONS header, then each
// named group of rows under its own sub-heading, closed by a single total for
// the whole column - the groups are not totalled separately.
const ComponentColumn = ({ head, groups, totalLabel, total }) => (
  <>
    <View style={styles.tableBox}>
      <View style={styles.tableHead}>
        <Text style={[styles.tableHeadText, { width: "58%" }]}>{head}</Text>
        <Text
          style={[styles.tableHeadText, { width: "42%", textAlign: "right" }]}
        >
          {`AMOUNT (${RUPEE})`}
        </Text>
      </View>
      {groups
        .filter((group) => group.items.length)
        .map((group) => (
          <React.Fragment key={group.title}>
            <View style={styles.subHead}>
              <Text style={styles.subHeadText}>{group.title}</Text>
            </View>
            {group.items.map((item, index) => (
              <View
                style={styles.tableRow}
                key={`${getComponentName(item)}-${index}`}
              >
                <Text style={styles.colName}>
                  {displayComponentName(item, group.title)}
                </Text>
                <Text style={styles.colAmount}>
                  {formatAmount(getComponentAmount(item))}
                </Text>
              </View>
            ))}
          </React.Fragment>
        ))}
    </View>
    <View style={styles.totalBar}>
      <Text style={styles.totalBarLabel}>{totalLabel}</Text>
      <Text style={styles.totalBarValue}>{formatMoney(total)}</Text>
    </View>
  </>
);

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

export const DetailedPayslipPage = ({ payslipData }) => {
  if (!payslipData) {
    return null;
  }

  const earningItems = normalizeItems(payslipData?.earnings);
  const otherEarningItems = normalizeItems(payslipData?.other_earnings);
  const deductionItems = normalizeItems(
    payslipData?.deduction,
    payslipData?.deductions,
  );
  const otherDeductionItems = normalizeItems(
    payslipData?.other_deduction,
    payslipData?.other_deductions,
    payslipData?.additional_deduction,
    payslipData?.additional_deductions,
  );

  const totalEarnings =
    pickNumber(payslipData?.total_earnings) ?? sumComponentAmounts(earningItems);
  const totalOtherEarnings =
    pickNumber(payslipData?.total_other_earnings) ??
    sumComponentAmounts(otherEarningItems);
  const totalDeductions =
    pickNumber(payslipData?.total_deductions, payslipData?.total_deduction) ??
    sumComponentAmounts(deductionItems);
  const totalOtherDeductions =
    pickNumber(
      payslipData?.total_other_deductions,
      payslipData?.total_other_deduction,
      payslipData?.total_additional_deductions,
    ) ?? sumComponentAmounts(otherDeductionItems);

  // The two TOTAL bars are the printed groups added up, so the salary summary
  // below them always reconciles against what the reader can see.
  const grossEarnings = totalEarnings + totalOtherEarnings;
  const allDeductions = totalDeductions + totalOtherDeductions;
  const netPay =
    pickNumber(payslipData?.net_pay, payslipData?.net_salary) ??
    Math.max(grossEarnings - allDeductions, 0);

  const totalDays = pickNumber(
    payslipData?.total_payable_shift,
    payslipData?.working_days,
    payslipData?.total_working_days,
  );
  const daysWorked = pickNumber(
    payslipData?.net_payable_shift,
    payslipData?.days_worked,
    payslipData?.number_of_days_worked,
  );
  const explicitLopDays = pickNumber(
    payslipData?.lop_days,
    payslipData?.lop_days_count,
    payslipData?.loss_of_pay_days,
    payslipData?.total_absent_days,
  );
  const lopDays =
    explicitLopDays !== null
      ? explicitLopDays
      : totalDays !== null && daysWorked !== null
        ? Math.max(totalDays - daysWorked, 0)
        : null;
  const holidays = pickNumber(
    payslipData?.holidays,
    payslipData?.holiday_days,
    payslipData?.total_holidays,
  );

  const otRows = buildOvertimeRows(payslipData, otherEarningItems);
  const otTotals = otRows.reduce(
    (total, row) => ({
      earningMinutes: total.earningMinutes + row.earningMinutes,
      deductionMinutes: total.deductionMinutes + row.deductionMinutes,
      netMinutes: total.netMinutes + row.netMinutes,
      amount: total.amount + row.amount,
    }),
    { earningMinutes: 0, deductionMinutes: 0, netMinutes: 0, amount: 0 },
  );

  const cycle = formatCycle(payslipData);

  const employeeFields = [
    { label: "Employee Name", value: payslipData?.user_name },
    ...(payslipData?.employee_id
      ? [{ label: "Employee ID", value: payslipData?.employee_id }]
      : []),
    ...(payslipData?.department_name
      ? [{ label: "Department", value: payslipData?.department_name }]
      : []),
    { label: "Designation", value: payslipData?.user_designation },
    { label: "PF Number", value: payslipData?.pf_no },
    { label: "ESI Number", value: payslipData?.esi_no },
    { label: "UAN Number", value: payslipData?.uan_no },
  ];

  const payrollFields = [
    { label: "Pay Period", value: formatPeriod(payslipData) },
    ...(cycle ? [{ label: "Pay Cycle", value: cycle }] : []),
    { label: "Pay Date", value: formatDate(payslipData?.pay_date) },
    ...(payslipData?.payment_mode
      ? [{ label: "Payment Mode", value: payslipData?.payment_mode }]
      : []),
    ...(payslipData?.bank_account_no
      ? [{ label: "Bank Account No", value: payslipData?.bank_account_no }]
      : []),
    { label: "Currency", value: payslipData?.currency || "INR" },
  ];

  const stripCells = [
    {
      icon: "wallet",
      label: "MONTHLY SALARY",
      value: formatMoney(payslipData?.monthly_salary),
    },
    {
      icon: "calendar",
      label: "TOTAL DAYS",
      value: totalDays === null ? "-" : formatAmount(totalDays, 0),
    },
    {
      icon: "person",
      label: "DAYS WORKED",
      value: daysWorked === null ? "-" : formatAmount(daysWorked, 1),
    },
    { symbol: RUPEE, label: "NET SALARY", value: formatMoney(netPay) },
  ];

  const attendanceRows = [
    { label: "Calendar Days", value: totalDays, decimals: 0 },
    { label: "Paid Days / Days Worked", value: daysWorked, decimals: 1 },
    { label: "LOP Days", value: lopDays, decimals: 1 },
    { label: "Holidays", value: holidays ?? 0, decimals: 0 },
  ];

  // The attendance table stands alone when the pay run produced no OT - it
  // keeps the earnings column's width rather than stretching across the sheet.
  const attendanceWidth = otRows.length ? "30%" : "49.2%";

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.card}>
        {/* ---- Masthead ---- */}
        <View style={styles.header}>
          {payslipData?.company_logo_url ? (
            <View style={styles.logoBox}>
              <Image style={styles.logo} src={secureImageUrl(payslipData.company_logo_url)} />
            </View>
          ) : null}
          <View style={styles.companyBlock}>
            <Text style={styles.companyName}>
              {payslipData?.company_name || "Organflow"}
            </Text>
            {payslipData?.company_address ? (
              <Text style={styles.companyLine}>
                {payslipData?.company_address}
              </Text>
            ) : null}
            <Text style={styles.companyLine}>Wage slip - Form - XIX</Text>
            {payslipData?.gstin ? (
              <Text style={styles.companyLine}>
                GSTIN : {payslipData?.gstin}
              </Text>
            ) : null}
          </View>
          <View style={styles.titleBlock}>
            <Text style={styles.payslipTitle}>PAYSLIP</Text>
            <Text style={styles.payslipMonth}>{formatPeriod(payslipData)}</Text>
          </View>
        </View>

        {/* ---- Employee / Payroll information ---- */}
        <View style={styles.row}>
          <InfoCard
            title="EMPLOYEE INFORMATION"
            icon="person"
            fields={employeeFields}
          />
          <View style={styles.gap} />
          <InfoCard
            title="PAYROLL INFORMATION"
            icon="calendar"
            fields={payrollFields}
          />
        </View>

        {/* ---- Headline figures ---- */}
        <View style={styles.strip} wrap={false}>
          {stripCells.map((cell, index) => (
            <View
              key={cell.label}
              style={[
                styles.stripCell,
                index < stripCells.length - 1 ? styles.stripDivider : null,
              ]}
            >
              <View style={styles.stripIcon}>
                {cell.symbol ? (
                  <Text style={styles.stripIconText}>{cell.symbol}</Text>
                ) : (
                  <Icon name={cell.icon} size={11} />
                )}
              </View>
              <View>
                <Text style={styles.stripLabel}>{cell.label}</Text>
                <Text style={styles.stripValue}>{cell.value}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* ---- Earnings | Deductions ---- */}
        <View style={styles.bodyRow}>
          <View style={styles.bodyCol}>
            <ComponentColumn
              head="EARNINGS"
              groups={[
                { title: "Fixed Earnings", items: earningItems },
                { title: "Other Earnings", items: otherEarningItems },
              ]}
              totalLabel="TOTAL EARNINGS"
              total={grossEarnings}
            />
          </View>

          <View style={styles.gap} />

          <View style={styles.bodyCol}>
            <ComponentColumn
              head="DEDUCTIONS"
              groups={[
                { title: "Mandatory Deductions", items: deductionItems },
                { title: "Other Deductions", items: otherDeductionItems },
              ]}
              totalLabel="TOTAL DEDUCTIONS"
              total={allDeductions}
            />
          </View>
        </View>

        {/* ---- Salary summary ---- */}
        <View style={styles.salaryBand} wrap={false}>
          <View style={styles.salaryRow}>
            <View style={styles.salaryTitleBlock}>
              <Chip icon="chart" />
              {/* Two lines by hand - "SALARY SUMMARY" is too wide for this
                  block and react-pdf would hyphenate it mid-word. */}
              <View style={styles.salaryTitle}>
                <Text style={styles.salaryTitleText}>SALARY</Text>
                <Text style={styles.salaryTitleText}>SUMMARY</Text>
              </View>
            </View>
            <View style={styles.salaryTerm}>
              <Text style={styles.salaryTermLabel}>Total Earnings</Text>
              <Text style={styles.salaryTermValue}>
                {formatMoney(grossEarnings)}
              </Text>
            </View>
            <Text style={styles.salaryOperator}>{MINUS}</Text>
            <View style={styles.salaryTerm}>
              <Text style={styles.salaryTermLabel}>Total Deductions</Text>
              <Text style={styles.salaryTermValue}>
                {formatMoney(allDeductions)}
              </Text>
            </View>
            <Text style={styles.salaryOperator}>=</Text>
            <View style={styles.salaryNet}>
              <Text style={styles.salaryNetLabel}>NET SALARY PAYABLE</Text>
              <Text style={styles.salaryNetValue}>{formatMoney(netPay)}</Text>
            </View>
          </View>
          {payslipData?.net_pay_in_words ? (
            <Text style={styles.salaryWords}>
              (Rupees {payslipData?.net_pay_in_words})
            </Text>
          ) : null}
        </View>

        {/* ---- Attendance summary | Overtime summary ---- */}
        <View style={styles.bodyRow}>
          <View style={[styles.tableBox, { width: attendanceWidth }]}>
            <View style={styles.cardHead}>
              <Chip icon="calendar" />
              <Text style={styles.cardTitle}>ATTENDANCE SUMMARY</Text>
            </View>
            <View style={styles.summaryHead}>
              <Text style={[styles.summaryHeadText, { width: "70%" }]}>
                PARTICULARS
              </Text>
              <Text
                style={[
                  styles.summaryHeadText,
                  { width: "30%", textAlign: "right" },
                ]}
              >
                DAYS
              </Text>
            </View>
            {attendanceRows.map((row) => (
              <View style={styles.summaryRow} key={row.label}>
                <Text style={[styles.summaryText, { width: "70%" }]}>
                  {row.label}
                </Text>
                <Text
                  style={[
                    styles.summaryText,
                    { width: "30%", textAlign: "right" },
                  ]}
                >
                  {row.value === null
                    ? "-"
                    : formatAmount(row.value, row.decimals)}
                </Text>
              </View>
            ))}
          </View>

          {otRows.length ? (
            <>
              <View style={styles.gap} />
              <View style={[styles.tableBox, { width: "68.4%" }]}>
                <View style={styles.cardHead}>
                  <Chip icon="clock" />
                  <Text style={styles.cardTitle}>OVERTIME SUMMARY</Text>
                </View>
                <View style={styles.summaryHead}>
                  {OT_COLUMNS.map((column) => (
                    <Text
                      key={column.key}
                      style={[
                        styles.summaryHeadText,
                        { width: column.width, textAlign: column.align },
                      ]}
                    >
                      {column.head}
                    </Text>
                  ))}
                </View>
                {otRows.map((row, index) => {
                  const values = {
                    type: row.name,
                    earning: formatTimeMinutes(row.earningMinutes),
                    deduction: formatTimeMinutes(row.deductionMinutes),
                    net: formatTimeMinutes(row.netMinutes),
                    rate: formatRate(row.rate),
                    amount: formatAmount(row.amount),
                  };
                  return (
                    <View style={styles.summaryRow} key={`${row.name}-${index}`}>
                      {OT_COLUMNS.map((column) => (
                        <Text
                          key={column.key}
                          style={[
                            styles.summaryText,
                            { width: column.width, textAlign: column.align },
                          ]}
                        >
                          {values[column.key]}
                        </Text>
                      ))}
                    </View>
                  );
                })}
                <View style={styles.summaryTotalRow}>
                  {OT_COLUMNS.map((column) => {
                    const totals = {
                      type: "TOTAL",
                      earning: formatTimeMinutes(otTotals.earningMinutes),
                      deduction: formatTimeMinutes(otTotals.deductionMinutes),
                      net: formatTimeMinutes(otTotals.netMinutes),
                      rate: "-",
                      amount: formatAmount(otTotals.amount),
                    };
                    return (
                      <Text
                        key={column.key}
                        style={[
                          styles.summaryTextBold,
                          { width: column.width, textAlign: column.align },
                        ]}
                      >
                        {totals[column.key]}
                      </Text>
                    );
                  })}
                </View>
              </View>
            </>
          ) : null}
        </View>

        {/* ---- Footer ---- */}
        <View style={styles.footer} wrap={false}>
          <Chip
            icon="document"
            style={styles.footerBadge}
            iconSize={11}
            iconColor={BLUE_TEXT}
          />
          <Text style={styles.footerNote}>
            This is a computer-generated payslip{"\n"}and does not require a
            signature.
          </Text>
          <View style={styles.footerRight}>
            <Text style={styles.footerLabel}>Generated on :</Text>
            <Text style={styles.footerValue}>
              {moment().format("DD-MMM-YYYY hh:mm A")}
            </Text>
          </View>
        </View>
      </View>
    </Page>
  );
};

const DetailedPayslipData = ({ payslipData }) => (
  <Document
    title={`${
      payslipData?.user_name ? payslipData?.user_name + " " : ""
    }${payslipData?.pay_schedule_name || "payslip"}`}
  >
    <DetailedPayslipPage payslipData={payslipData} />
  </Document>
);

export const BulkDetailedPayslipData = ({ employeesPdfData = [] }) => (
  <Document>
    {employeesPdfData
      .filter((emp) => emp?.payslipData)
      .map((employee, index) => (
        <DetailedPayslipPage key={index} payslipData={employee.payslipData} />
      ))}
  </Document>
);

export default DetailedPayslipData;
